import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import type IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";

import type { LocalSettings } from "@app/config/LocalSettings";
import localSettings from "@app/config/LocalSettings";
import TimelineEventType from "@app/scene/dashboard/enum/TimelineEventType";
import StackRankDrillDownViewState from "@app/scene/dashboard/store/stackRank/DrillDownViewState";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import { parseApiDate } from "@app/util/parsers";
import standardRangeToDateRange from "@app/util/range/standardRangeToDateRange";

import StackRankViewState from "../store/stackRank/StackRankViewState";

class DashboardSettings {
  settings: LocalSettings;

  private readonly VISIBLE_EVENT_TYPES = "__DASHBOARD_VISIBLE_EVENT_TYPES__";

  //  stack rank view state
  private readonly STACK_RANK_VIEW_STATE = "__STACK_RANK_VIEW_STATEv2__";

  private readonly STACK_RANK_DRILLDOWN_VIEW_STATE = "__STACK_RANK_DRILLDOWN_VIEW_STATE__";

  private readonly DASHBOARD_TABS_ORDER = "__DASHBOARD_TABS_ORDER__";

  private readonly FI_COLUMN_ORDER = "__DASHBOARD_FI_COLUMN_ORDER__";

  private readonly WORD_NOTES_EXPORT_CONFIRMATION = "__WORD_NOTES_EXPORT_CONFIRMATION__";

  constructor(settings: LocalSettings) {
    this.settings = settings;
  }

  getFieldInsightColumnOrder = (chartId: number): string[] => {
    try {
      const json = this.settings.getValue(this.FI_COLUMN_ORDER);
      const map = json ? (JSON.parse(json as string) as Record<string, string[]>) : {};
      const order = map[chartId];
      return Array.isArray(order) ? order : [];
    } catch {
      return [];
    }
  };

  setFieldInsightColumnOrder = (chartId: number, order: string[]) => {
    const json = this.settings.getValue(this.FI_COLUMN_ORDER);
    let map: Record<string, string[]> = {};
    try {
      map = json ? (JSON.parse(json as string) as Record<string, string[]>) : {};
    } catch {
      map = {};
    }
    map[chartId] = order;
    this.settings.setValue(this.FI_COLUMN_ORDER, JSON.stringify(map));
  };

  getDashboardTabsOrder = (): string[] => {
    try {
      const tabsOrder = localStorage.getItem(this.DASHBOARD_TABS_ORDER);
      return tabsOrder ? JSON.parse(tabsOrder) : [];
    } catch {
      return [];
    }
  };

  setDashboardTabsOrder = (tabsOrder: string[]) => {
    localStorage.setItem(this.DASHBOARD_TABS_ORDER, JSON.stringify(tabsOrder));
  };

  getVisibleEventTypes = (): TimelineEventType[] => {
    const json = this.settings.getValue(this.VISIBLE_EVENT_TYPES);
    const allTypes = Object.values(TimelineEventType);
    try {
      let types = json ? JSON.parse(json as string) : allTypes;
      types = Array.isArray(types) ? types.filter((type) => allTypes.includes(type)) : types;
      return !Array.isArray(types) || !types.length ? allTypes : types;
    } catch {
      return allTypes;
    }
  };

  setVisibleEventTypes = (eventTypes: TimelineEventType[]) => {
    this.settings.setValue(this.VISIBLE_EVENT_TYPES, JSON.stringify(eventTypes));
  };

  getDontShowWordNotesExportConfirmation = (): boolean => {
    return !!this.settings.getValue(
      `${this.WORD_NOTES_EXPORT_CONFIRMATION}${this.settings.getUserId()}`
    );
  };

  setDontShowWordNotesExportConfirmation = () => {
    this.settings.setValue(
      `${this.WORD_NOTES_EXPORT_CONFIRMATION}${this.settings.getUserId()}`,
      "1"
    );
  };

  getStackRankViewState = (): StackRankViewState | undefined => {
    const json = localStorage.getItem(this.STACK_RANK_VIEW_STATE);
    if (!json) {
      return undefined;
    }

    try {
      const viewState: Omit<StackRankViewState, "rankingMetric"> & {
        rankingMetric: string;
      } = JSON.parse(json);

      const rankingMetric =
        leaderboardFieldModel.getByName(viewState.rankingMetric) ??
        leaderboardFieldModel.getByName(LeaderboardMetricFieldName.CHECK_INS_COMPLETED)!;

      if (viewState.dateRangeType === DateRangeType.CUSTOM) {
        if (typeof viewState.dateRange.startDate === "string") {
          // both these start and end fields can be strings after parsing json
          // converting them back in Dates here
          viewState.dateRange.startDate = parseApiDate(viewState.dateRange.startDate);
        }
        if (typeof viewState.dateRange.endDate === "string") {
          viewState.dateRange.endDate = parseApiDate(viewState.dateRange.endDate);
        }
      } else {
        viewState.dateRange =
          standardRangeToDateRange(viewState.dateRangeType as StandardRange) ??
          standardRangeToDateRange(`${DateRangeType.WEEK}0`)!;
      }
      return { ...viewState, rankingMetric };
    } catch {
      return undefined;
    }
  };

  setStackRankViewState = (viewState: StackRankViewState) => {
    localStorage.setItem(
      this.STACK_RANK_VIEW_STATE,
      JSON.stringify({ ...viewState, rankingMetric: viewState.rankingMetric.name })
    );
  };

  getStackRankDrillDownViewState = (): StackRankDrillDownViewState => {
    const defaultValue: StackRankDrillDownViewState = {
      columns: leaderboardFieldModel.getDefaultListViewState().columns,
      grouping: "user",
    };

    const json = localStorage.getItem(this.STACK_RANK_DRILLDOWN_VIEW_STATE);
    if (!json) {
      return defaultValue;
    }

    try {
      // parse JSON converting every field name into a field instance
      const viewState: StackRankDrillDownViewState = JSON.parse(json, (key, value) =>
        key === "field" ? leaderboardFieldModel.getByName(value) : value
      );

      const columnVisibility = new Map(
        viewState.columns.map(({ field, visible }) => [field.name, visible])
      );

      // let's re-calculate the columns, as there might be new custom metrics created since last time
      viewState.columns = leaderboardFieldModel.getDefaultListViewState().columns.map((column) => ({
        ...column,
        // we preserve previous visibility if it was set
        visible: columnVisibility.get(column.field.name) ?? column.visible,
      }));

      return viewState;
    } catch {
      return defaultValue;
    }
  };

  setStackRankDrillDownViewState = (viewState: StackRankDrillDownViewState) => {
    // convert all Field instances to field names when saving
    const viewStateJson = JSON.stringify(viewState, (key, value) =>
      key === "field" ? (value as IField).name : value
    );
    localStorage.setItem(this.STACK_RANK_DRILLDOWN_VIEW_STATE, viewStateJson);
  };
}

const dashboardSettings = new DashboardSettings(localSettings);

export default dashboardSettings;
